import React, { useState, useEffect } from 'react';
import { ComposedChart, Line, ResponsiveContainer, YAxis, Tooltip } from 'recharts';
import { X, Network, Activity, Copy, Download, Maximize2 } from 'lucide-react';
import type { StreamStats } from '../types';
import type { ChartPoint } from '../hooks/useStats';
import { statsService } from '../core/StatsService';
import { eventBus } from '../core/EventBus';
import { formatHostRelay } from '../utils/relayDisplay';

interface StatsPanelProps {
  stats: StreamStats;
  chartData: ChartPoint[];
  onClose: () => void;
  onExportReport?: () => void;
  onOpenIceDetail?: () => void;  // 打开 ICE 详情回看面板
  onExpand?: () => void;  // Stage 2 占位
  isSettingsOpen?: boolean;
  encoderBitrate?: number;  // Host 编码器目标码率 (bps)
}

interface StatBoxProps {
  label: string;
  value: string | number;
  unit?: string;
  warning?: boolean;
  tooltip?: string;  // hover 显示详细说明
}

const StatBox: React.FC<StatBoxProps> = ({ label, value, unit, warning = false, tooltip }) => (
  <div
    className={`bg-white/5 p-2 rounded-xl border border-white/5 text-center ${tooltip ? 'cursor-help' : ''}`}
    title={tooltip}
  >
    <div className="text-[9px] text-zinc-500 uppercase font-bold">{label}</div>
    <div className={`text-sm font-mono font-bold ${warning ? 'text-amber-500' : 'text-white'}`}>
      {value}
      {unit && <span className="text-[8px] opacity-40 font-normal ml-0.5">{unit}</span>}
    </div>
  </div>
);

interface ProgressBarProps {
  label: string;
  value: number;
  maxValue?: number;
  color: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ label, value, maxValue = 15, color }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between text-[10px]">
      <span className="text-zinc-400 font-medium">{label}</span>
      <span className="font-mono text-zinc-200">{value < 1 ? value.toFixed(2) : value.toFixed(1)}ms</span>
    </div>
    <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
      <div
        className={`h-full transition-all duration-1000 ${color}`}
        style={{ width: `${Math.min((value / maxValue) * 100, 100)}%` }}
      />
    </div>
  </div>
);

// 5 条主曲线的 dataKey（与 chart 中 Line 的 dataKey 对应）
type SeriesKey = 'fps' | 'bitrate' | 'rtt' | 'packetLoss' | 'jitterBuffer';
type SeriesHidden = Record<SeriesKey, boolean>;

const StatsPanel: React.FC<StatsPanelProps> = (props) => {
  const { stats, chartData, onClose, onExportReport, onOpenIceDetail, onExpand, isSettingsOpen = false, encoderBitrate = 0 } = props;
  const [copied, setCopied] = useState(false);

  // 实时趋势 5 条曲线显隐状态（点击图例切换）
  const [hiddenSeries, setHiddenSeries] = useState<SeriesHidden>({
    fps: false, bitrate: false, rtt: false, packetLoss: false, jitterBuffer: false,
  });
  const toggleSeries = (k: SeriesKey) =>
    setHiddenSeries(h => ({ ...h, [k]: !h[k] }));

  // 判断是否为软件编码（CPU）
  // 阶段 2：优先用 catalog active.accel；兜底正则（旧 host 或 catalog 还没到达时）
  const _ssAccel = (() => {
    const ss = statsService.serverStats;
    if (ss?.encoder_catalog && ss.active_encoder_id) {
      const e = ss.encoder_catalog.find((x) => x.id === ss.active_encoder_id);
      if (e) return e.accel;
    }
    return null;
  })();
  const isSoftwareEncoder = _ssAccel
    ? _ssAccel !== 'GPU'
    : /x264|openh264|cpu|software/i.test(stats.encoderName);
  const encodeLabel = isSoftwareEncoder ? '软件编码' : '硬件编码';

  // 实时单帧采集耗时（来自 server_stats.capture_frame_avg_us，1s 滚动平均，微秒）
  // 与「采集→编码」互补：本字段是 capturer 自身耗时，「采集→编码」是 capturer 完成到编码处理的流水线 lag
  const [captureFrameAvgUs, setCaptureFrameAvgUs] = useState<number>(0);
  useEffect(() => {
    const unsub = eventBus.on('stats:serverStats', (s: { capture_frame_avg_us?: number }) => {
      if (typeof s?.capture_frame_avg_us === 'number') {
        setCaptureFrameAvgUs(s.capture_frame_avg_us);
      }
    });
    return () => { unsub(); };
  }, []);

  // 读取编码器实际输出码率（优先用 actual_encode_kbps，没有则回退到 BWE 目标）
  const serverStats = statsService.serverStats;
  const hostRelay = formatHostRelay(serverStats);
  const actualEncodeBitrate = serverStats?.actual_encode_kbps
    ? serverStats.actual_encode_kbps
    : (encoderBitrate / 1000);  // bps → kbps

  const handleCopy = async () => {
    // 使用 StatsService.generateReport() 生成管线分段格式的报告
    const report = statsService.generateReport();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(report);
      } else {
        // Fallback: 非 HTTPS 环境下 navigator.clipboard 不可用
        const textarea = document.createElement('textarea');
        textarea.value = report;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('[StatsPanel] Copy failed:', e);
    }
  };

  // When settings panel is open, position stats panel to the left of it
  // Settings panel: right-4 (16px), w-80 (320px) = takes 336px from right
  // Stats panel: position at right-[356px] when settings open, otherwise right-8
  // z-[85] to be above SettingsPanel backdrop (z-[80]) but below panel (z-[90])
  return (
    <div className={`fixed top-4 bottom-4 z-[85] pointer-events-none transition-all duration-300 ${
      isSettingsOpen ? 'right-[356px]' : 'right-8'
    }`}>
      <div className="w-80 h-full glass-high rounded-3xl p-5 shadow-4xl border border-white/5 animate-slide-right pointer-events-auto flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
        <h3 className="text-[11px] font-black tracking-widest text-zinc-500 uppercase flex items-center gap-2">
          <Activity size={14} className="text-blue-500" /> 引擎诊断报告
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onExpand}
            title="打开完整诊断面板"
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <Maximize2 size={14} className="text-zinc-400" />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded-full transition-colors">
            <X size={16} className="text-zinc-500" />
          </button>
        </div>
        </div>

        <div className="space-y-5 overflow-y-auto flex-1 pr-1 custom-scrollbar">
        {/* Key Metrics Grid - 3 rows x 3 cols */}
        <div className="grid grid-cols-3 gap-2">
          <StatBox
            label="接收码率"
            value={stats.bitrate >= 1000 ? (stats.bitrate / 1000).toFixed(1) : stats.bitrate}
            unit={stats.bitrate >= 1000 ? 'Mbps' : 'kbps'}
            tooltip="浏览器 RTCP 接收侧码率（含 RTP overhead 和 NACK 重传，比 host 编码码率略高）"
          />
          <StatBox
            label="编码码率"
            value={actualEncodeBitrate >= 1000 ? (actualEncodeBitrate / 1000).toFixed(1) : Math.round(actualEncodeBitrate)}
            unit={actualEncodeBitrate >= 1000 ? 'Mbps' : 'kbps'}
            tooltip="host 端编码器实际输出 bitstream 的字节速率（1 秒滑窗）"
          />
          <StatBox label="RTT" value={stats.rtt} unit="ms" warning={stats.rtt > 50}
            tooltip="Round Trip Time 往返时延（来自 RTCP currentRoundTripTime）。> 50ms 黄色提示" />
          <StatBox label="帧率" value={stats.fps} unit="FPS" warning={stats.fps < 45}
            tooltip="浏览器接收并解码的帧率（framesDecoded 1s 增量）。< 45 黄色提示" />
          <StatBox label="丢包" value={stats.packetLoss.toFixed(2)} unit="%" warning={stats.packetLoss > 1}
            tooltip="接收端 RTP 丢包率（packetsLost / packetsReceived）。> 1% 黄色提示" />
          <StatBox label="冻结" value={stats.freezeCount} unit="次" warning={stats.freezeCount > 0}
            tooltip={`画面冻结累计次数（freezeCount, > 1s 视为冻结）。累计时长 ${stats.totalFreezesDuration.toFixed(2)}s`} />
          {/* ===== 诊断字段（Stage 1）===== */}
          {(() => {
            const qp = serverStats?.encoder_qp;
            // -1 / undefined / 0 → N/A；0 通常是 lock_bs.frameAvgQP 未填的退化值
            const qpNA = qp === undefined || qp === -1 || qp === 0;
            const qpVal = qpNA ? 'N/A' : String(qp);
            const isAv1 = /AV1/i.test(stats.encoderName || '');
            const qpWarn = !qpNA && !isAv1 && qp! >= 41;
            const qpTip = qpNA
              ? 'QP 暂未采集（libaom AV1 host 不暴露 QP）'
              : isAv1
              ? `NVENC AV1 frameAvgQP（范围 0-255）。> 160 黄、> 200 红`
              : `编码器最近一帧平均 QP（H264/H265/x264 范围 0-51）。> 41 黄、> 46 红。低值=画质好，高值=BWE 不够`;
            return <StatBox label="QP" value={qpVal} warning={qpWarn} tooltip={qpTip} />;
          })()}
          {(() => {
            const buf = serverStats?.vbv_buf_kbits ?? 0;
            const capped = serverStats?.vbv_capped === true;
            const noVbvCodec = /libaom|openh264/i.test(stats.encoderName || '');
            if (noVbvCodec) {
              return <StatBox label="VBV" value="N/A"
                tooltip="libaom AV1 / OpenH264 不走 NVENC/x264 的 VBV 接口，host 端不可见" />;
            }
            const tip = capped
              ? `VBV 缓冲区已撞 1500kb 上限（仅 x264）。raw_bufsize > 1500kb 触发，单帧封顶`
              : `编码器 VBV 缓冲大小。NVENC: bitrate/fps × 3 帧。x264: per_frame × multiplier。${buf} kb`;
            return (
              <StatBox
                label="VBV"
                value={buf > 0 ? buf : '-'}
                unit={buf > 0 ? (capped ? 'kb·CAP' : 'kb') : ''}
                warning={capped}
                tooltip={tip}
              />
            );
          })()}
          {(() => {
            const ev = statsService.getEvents30s();
            const total = ev.bwe_drop_30plus + ev.vbv_switch + ev.sudden_drop +
                          ev.cpu_overload + ev.encoder_recovery + ev.large_frame;
            const tipLines: string[] = ['最近 30 秒系统压力事件总数（6 类事件累加）'];
            if (ev.bwe_drop_30plus)   tipLines.push(`• BWE 跌幅 ≥30%: ${ev.bwe_drop_30plus} 次`);
            if (ev.vbv_switch)        tipLines.push(`• x264 VBV 切档: ${ev.vbv_switch} 次`);
            if (ev.sudden_drop)       tipLines.push(`• x264 BWE 突变保护: ${ev.sudden_drop} 次`);
            if (ev.cpu_overload)      tipLines.push(`• CPU 过载: ${ev.cpu_overload} 次`);
            if (ev.encoder_recovery)  tipLines.push(`• 编码器重建: ${ev.encoder_recovery} 次`);
            if (ev.large_frame)       tipLines.push(`• 大帧 >50KB: ${ev.large_frame} 次`);
            if (tipLines.length === 1) tipLines.push('（当前无事件）');
            tipLines.push('详细分类见 Dashboard "保护机制" 卡片');
            return <StatBox label="事件30s" value={total} unit="次" warning={total > 5}
              tooltip={tipLines.join('\n')} />;
          })()}
        </div>

        {/* 实时趋势 - 多曲线时间线对齐（Stage 1：三图合一）*/}
        <div className="space-y-2">
          <div className="text-[10px] text-zinc-500 uppercase font-bold flex justify-between px-1">
            <span>实时趋势</span>
            <span className={stats.transportType === 'p2p' ? 'text-emerald-500' : 'text-amber-500'}>
              {stats.transportType === 'p2p' ? '优 (P2P)' : '中转'}
            </span>
          </div>
          {/* Legend：点击切换对应曲线显示/隐藏 */}
          <div className="flex gap-2 text-[9px] px-1 flex-wrap">
            {([
              { key: 'fps' as SeriesKey,          dot: 'bg-emerald-500', label: 'FPS' },
              { key: 'bitrate' as SeriesKey,      dot: 'bg-blue-500',    label: '码率' },
              { key: 'rtt' as SeriesKey,          dot: 'bg-violet-500',  label: 'RTT' },
              { key: 'packetLoss' as SeriesKey,   dot: 'bg-red-500',     label: '丢包' },
              { key: 'jitterBuffer' as SeriesKey, dot: 'bg-amber-500',   label: '抖动' },
            ]).map(item => {
              const isHidden = hiddenSeries[item.key];
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => toggleSeries(item.key)}
                  title={isHidden ? '点击显示' : '点击隐藏'}
                  className={`flex items-center gap-1 cursor-pointer transition-opacity hover:opacity-80 ${
                    isHidden ? 'opacity-40 line-through' : ''
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${item.dot}`} style={{ opacity: isHidden ? 0.3 : 1 }} />
                  {item.label}
                </button>
              );
            })}
          </div>
          <div className="h-[120px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              {/* 默认抽屉只显示最近 60 秒（轻量）；Dashboard 展开模式可显示完整 5min */}
              <ComposedChart data={chartData.slice(-60)}>
                {/* 三个 Y 轴：left=fps/rtt/jitter, right=bitrate, loss=packetLoss% */}
                <YAxis yAxisId="left" hide domain={[0, 'auto']} />
                <YAxis yAxisId="right" hide domain={[0, 'auto']} orientation="right" />
                <YAxis yAxisId="loss" hide domain={[0, 100]} />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(0,0,0,0.85)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    fontSize: '11px',
                  }}
                  labelStyle={{ display: 'none' }}
                  formatter={(value: number, name: string) => {
                    if (name === 'fps') return [`${value} FPS`, '帧率'];
                    if (name === 'bitrate') return [`${(value / 1000).toFixed(2)} Mbps`, '码率'];
                    if (name === 'rtt') return [`${value} ms`, 'RTT'];
                    if (name === 'packetLoss') return [`${value.toFixed(2)}%`, '丢包'];
                    if (name === 'jitterBuffer') return [`${value.toFixed(1)} ms`, '抖动'];
                    return [value, name];
                  }}
                />
                <Line yAxisId="left" type="monotone" dataKey="fps" stroke="#10b981" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hiddenSeries.fps} />
                <Line yAxisId="right" type="monotone" dataKey="bitrate" stroke="#3b82f6" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hiddenSeries.bitrate} />
                <Line yAxisId="left" type="monotone" dataKey="rtt" stroke="#8b5cf6" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hiddenSeries.rtt} />
                <Line yAxisId="loss" type="monotone" dataKey="packetLoss" stroke="#ef4444" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hiddenSeries.packetLoss} />
                <Line yAxisId="left" type="monotone" dataKey="jitterBuffer" stroke="#f59e0b" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hiddenSeries.jitterBuffer} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Latency Breakdown */}
        <div className="space-y-3">
          <div className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-2">
            <Network size={12} /> 延迟分解
          </div>
          <div className="space-y-2.5">
            {/* 实时采集（capturer 自身耗时；与「采集→编码」流水线 lag 互补）*/}
            <ProgressBar label="实时采集" value={captureFrameAvgUs / 1000} color="bg-teal-500" />
            {/* Stage 2B.7: 采集→编码 lag（host 端 capture_to_encode_lag p50）*/}
            <ProgressBar label="采集→编码" value={stats.latencyBreakdown.capture} color="bg-cyan-500" />
            <ProgressBar label={encodeLabel} value={stats.latencyBreakdown.encode} color="bg-indigo-500" />
            <ProgressBar label="网络传输" value={stats.latencyBreakdown.network} color="bg-violet-500" />
            <ProgressBar label="抖动缓冲" value={stats.latencyBreakdown.jitterBuffer} color="bg-purple-500" />
            <ProgressBar label="硬件解码" value={stats.latencyBreakdown.decode} color="bg-emerald-500" />
          </div>
          <div className="flex justify-between text-xs pt-2 border-t border-white/5">
            <span className="text-zinc-400">总延迟</span>
            <span className={`font-mono font-bold ${stats.latencyBreakdown.total > 50 ? 'text-amber-500' : 'text-white'}`}>
              {stats.latencyBreakdown.total.toFixed(1)} ms
            </span>
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-[10px] text-zinc-500 space-y-1 pt-2 border-t border-white/5">
          <div className="flex justify-between">
            <span>分辨率</span>
            <span className="text-zinc-300">{stats.resolution}</span>
          </div>
          <div className="flex justify-between">
            <span>编码器</span>
            <span className="text-zinc-300">{stats.encoderName}</span>
          </div>
          <div className="flex justify-between">
            <span>捕获模式</span>
            <span className="text-zinc-300">{stats.captureMode}</span>
          </div>
          <div className="flex justify-between">
            <span>连接类型</span>
            <span className={stats.transportType === 'p2p' ? 'text-emerald-500 font-bold' : 'text-amber-500 font-bold'}>
              {stats.transportType === 'p2p' ? 'P2P' : stats.transportType === 'relay' ? 'TURN' : '未知'}
            </span>
          </div>
          {stats.transportType === 'relay' && stats.relayServer && (
            <div className="flex justify-between">
              <span>浏览器中转</span>
              <span className="text-zinc-300 font-mono">{stats.relayServer}</span>
            </div>
          )}
          {stats.transportType === 'relay' && hostRelay && (
            <div className="flex justify-between">
              <span>Host中转</span>
              <span className="text-zinc-300 font-mono text-right truncate max-w-[210px]">{hostRelay}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>远程地址</span>
            <span className="text-zinc-300 font-mono">{stats.remoteAddress || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span>远程GPU</span>
            <span className="text-zinc-300">{stats.hostGpu || 'Unknown'}</span>
          </div>
          <div className="flex justify-between">
            <span>Host版本</span>
            <span className="text-zinc-300 font-mono">{stats.hostVersion || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span>显示模式</span>
            <span className={serverStats?.display_mode === 'virtual' ? 'text-amber-400' : serverStats?.display_mode === 'none' ? 'text-red-400 font-bold' : 'text-zinc-300'}>
              {serverStats?.display_mode === 'virtual' ? '虚拟显示器' : serverStats?.display_mode === 'none' ? '无显示器(降级)' : serverStats?.display_mode === 'physical' ? '物理显示器' : '-'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleCopy}
            className="flex-1 py-2 px-2 whitespace-nowrap bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-xs font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5"
          >
            <Copy size={12} />
            {copied ? '已复制' : '复制'}
          </button>
          {onExportReport && (
            <button
              onClick={onExportReport}
              className="flex-1 py-2 px-2 whitespace-nowrap bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-xs font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5"
            >
              <Download size={12} />
              导出
            </button>
          )}
          {onOpenIceDetail && (
            <button
              onClick={onOpenIceDetail}
              className="flex-1 py-2 px-2 whitespace-nowrap bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-xs font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5"
            >
              <Network size={12} />
              ICE
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(StatsPanel);
